import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, RefreshCw, Download } from "lucide-react";
import { marked } from "marked";

/**
 * Kullanıcı Dokümanı sayfası.
 * `/app/memory/USER_GUIDE.md` dosyasını GET /api/docs/user-guide üzerinden
 * canlı olarak okur — her deploy sonrası güncel içerik otomatik yansır.
 */
export default function HelpDocs() {
  const [content, setContent] = useState("");
  const [updatedAt, setUpdatedAt] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/docs/user-guide");
      setContent(data.content || "");
      setUpdatedAt(data.updated_at || "");
    } catch (e) {
      toast.error("Doküman yüklenemedi: " + (e?.response?.data?.detail || e?.message));
      setContent("");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const downloadMd = () => {
    const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "plasper-kullanici-dokumani.md";
    a.click();
    URL.revokeObjectURL(url);
  };

  const html = React.useMemo(() => {
    if (!content) return "";
    try {
      marked.setOptions({ gfm: true, breaks: false, headerIds: true });
      return marked.parse(content);
    } catch {
      return `<pre>${content}</pre>`;
    }
  }, [content]);

  const updatedTxt = updatedAt
    ? new Date(updatedAt).toLocaleString("tr-TR", { dateStyle: "long", timeStyle: "short" })
    : "—";

  return (
    <div className="space-y-4" data-testid="docs-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={20} />
            <h1 className="font-display text-3xl font-bold">Kullanıcı Dokümanı</h1>
          </div>
          <p className="text-sm text-neutral-500">
            Uygulamadaki tüm modüller ve alanlar için kılavuz. Canlıya yeni sürüm çıktığında bu içerik otomatik güncellenir.
          </p>
          <div className="text-[11px] text-neutral-400 mt-1 font-mono-num">
            Son güncelleme: {updatedTxt}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="docs-refresh"
            disabled={loading}
            className="border border-black/30 px-3 py-2 text-xs uppercase tracking-widest hover:bg-neutral-50 flex items-center gap-1.5"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Yenile
          </button>
          <button
            onClick={downloadMd}
            data-testid="docs-download"
            disabled={!content}
            className="border border-black bg-black text-white px-3 py-2 text-xs uppercase tracking-widest hover:opacity-90 disabled:opacity-40 flex items-center gap-1.5"
          >
            <Download size={12} /> Markdown İndir
          </button>
        </div>
      </header>

      <div className="brutal-card p-6">
        {loading ? (
          <div className="text-center py-16 text-neutral-500">Yükleniyor…</div>
        ) : content ? (
          <div className="docs-prose" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="text-center py-16 text-neutral-500">
            Doküman bulunamadı. Yönetici doküman dosyasını eklemeli.
          </div>
        )}
      </div>

      {/* Basit tipografi + tablo stilleri (Tailwind prose bağımlılığı olmadan) */}
      <style>{`
        .docs-prose { max-width: 780px; margin: 0 auto; color: #111; line-height: 1.65; font-size: 14px; }
        .docs-prose h1 { font-size: 28px; font-weight: 700; margin: 24px 0 10px; letter-spacing: -0.01em; }
        .docs-prose h2 { font-size: 22px; font-weight: 700; margin: 28px 0 8px; padding-bottom: 4px; border-bottom: 1px solid rgba(0,0,0,0.1); }
        .docs-prose h3 { font-size: 17px; font-weight: 700; margin: 20px 0 6px; }
        .docs-prose h4 { font-size: 15px; font-weight: 700; margin: 16px 0 4px; text-transform: uppercase; letter-spacing: 0.06em; color:#444; }
        .docs-prose p { margin: 8px 0; }
        .docs-prose a { color: #0f766e; text-decoration: underline; }
        .docs-prose ul, .docs-prose ol { margin: 8px 0 8px 20px; }
        .docs-prose li { margin: 3px 0; }
        .docs-prose code { background: #f4f4f5; padding: 1px 5px; font-size: 12.5px; border: 1px solid #e5e7eb; border-radius: 2px; font-family: ui-monospace,SFMono-Regular,Menlo,monospace; }
        .docs-prose pre { background: #0a0a0a; color: #fff; padding: 14px; overflow-x: auto; font-size: 12.5px; }
        .docs-prose pre code { background: transparent; color: inherit; border: 0; padding: 0; }
        .docs-prose blockquote { border-left: 3px solid #0f766e; padding: 4px 10px; color: #444; background: #f0fdfa; margin: 10px 0; font-size: 13.5px; }
        .docs-prose table { border-collapse: collapse; margin: 12px 0; width: 100%; font-size: 12.5px; }
        .docs-prose th, .docs-prose td { border: 1px solid #d4d4d8; padding: 6px 10px; text-align: left; vertical-align: top; }
        .docs-prose th { background: #f5f5f5; font-weight: 700; }
        .docs-prose hr { border: 0; border-top: 1px solid #e5e7eb; margin: 24px 0; }
        .docs-prose strong { font-weight: 700; }
      `}</style>
    </div>
  );
}
